import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ssoError, setSsoError] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('sso_error') || null;
  });
  const [ssoExchangePending] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('sso_code') || null;
  });

  // Clean URL of SSO params immediately
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('sso_code') || params.get('sso_error')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Exchange one-time SSO code — sets httpOnly cookie on the backend
  useEffect(() => {
    if (!ssoExchangePending) return;
    api.post('/auth/sso/exchange', { code: ssoExchangePending })
      .then(() => fetchUser())
      .catch(() => {
        setSsoError('SSO login failed. Please try again.');
        setLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On load, try to restore session from cookie
  useEffect(() => {
    if (ssoExchangePending) return;
    fetchUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Register interceptor once — clears user on 401 from protected endpoints.
  // Skip /auth/* routes to avoid infinite loop (logout itself returns 401 when unauthenticated).
  useEffect(() => {
    const id = api.interceptors.response.use(
      (response) => response,
      (error) => {
        const url = error.config?.url || '';
        if (error.response?.status === 401 && !url.startsWith('/auth/')) {
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUser = async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (userData) => {
    const res = await api.post('/auth/register', userData);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    api.post('/auth/logout').catch(() => {});
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const isManager = user?.role === 'manager';
  const canApprove = isAdmin || isManager;
  const canViewReports = isAdmin || user?.role === 'finance_viewer' || user?.role === 'leadership_viewer';
  const isViewer = user?.role === 'finance_viewer' || user?.role === 'leadership_viewer';

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      api,
      login,
      register,
      logout,
      updateUser,
      isAdmin,
      isSuperAdmin,
      isManager,
      canApprove,
      canViewReports,
      isViewer,
      isAuthenticated: !!user,
      ssoError,
      clearSsoError: () => setSsoError(null),
    }}>
      {children}
    </AuthContext.Provider>
  );
};
